/**
 * Point-cloud registry for Free-path modules.
 *
 * The AT2050 / ATH2050 180° U-turn is sampled at Δs = 0.1 mm with 5001
 * points covering [0..500] mm of arc length. The data ships as JSON files
 * co-located with this module under ./data/ — they're imported via
 * JSON-resolution so consumers don't have to fetch them at runtime.
 *
 * Y-axis convention in the JSON is 2D-canvas-down (positive = "into the
 * curve"). XtsPointCloudCurve3 applies the Y-flip when projecting into the
 * 3D RH-Z-up frame.
 *
 * Build note: `tsc` does not emit imported .json files. A postbuild copy
 * step (scripts/copy-runtime-data.mjs) mirrors src/geometry/data/*.json
 * into dist/geometry/data/ so the relative imports in the compiled output
 * resolve against the published tarball.
 */

import combined from './data/at2050-180-combined.path.json' with { type: 'json' };
import half0 from './data/at2050-0500-half.path.json' with { type: 'json' };
import half1 from './data/at2050-0501-half.path.json' with { type: 'json' };

export interface PointCloud {
  /** Total arc length in mm (= (N-1) × samplingMm). */
  lengthMm: number;
  /** Constant arc-length spacing between samples, in mm (always 0.1 here). */
  samplingMm: number;
  /** [x_i, y_i] in mm; canvas Y-down convention. */
  points: ReadonlyArray<readonly [number, number]>;
}

interface RawPathFile {
  lengthMm: number;
  samplingMm: number;
  axisYDirection: 'down' | 'up';
  points: number[][];
}

function freeze(p: RawPathFile): PointCloud {
  // Defensive: ensure every entry is a tuple-of-two numbers.
  const pts: Array<readonly [number, number]> = p.points.map((pt) => {
    if (!pt || pt.length < 2) {
      throw new Error('PointCloud entry must be [x, y]');
    }
    return [pt[0]!, pt[1]!] as const;
  });
  return Object.freeze({
    lengthMm: p.lengthMm,
    samplingMm: p.samplingMm,
    points: Object.freeze(pts),
  });
}

const REGISTRY: Record<string, PointCloud> = {
  AT2050_180_COMBINED: freeze(combined as RawPathFile),
  AT2050_0500_HALF: freeze(half0 as RawPathFile),
  AT2050_0501_HALF: freeze(half1 as RawPathFile),
};

export function getPointCloud(id: string): PointCloud | undefined {
  return REGISTRY[id];
}

export function registerPointCloud(id: string, cloud: PointCloud): void {
  REGISTRY[id] = cloud;
}

/**
 * Remove a previously-registered cloud. Returns `true` when an entry was
 * removed, `false` when the id was not registered. Useful for test cleanup
 * and hot-reload scenarios where a calibration tool re-registers clouds.
 */
export function unregisterPointCloud(id: string): boolean {
  if (!(id in REGISTRY)) return false;
  delete REGISTRY[id];
  return true;
}

export const POINT_CLOUDS: Readonly<Record<string, PointCloud>> = REGISTRY;
